import { useState, useEffect, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import PhygitalIcon, { PoweredByPhygital } from "@/components/PhygitalIcon";
import PageMeta from "@/components/PageMeta";
import {
  Scissors,
  Sparkles,
  Palette,
  Eye,
  PartyPopper,
  User,
  AlertCircle,
  Check,
  Copy,
  Download,
  ExternalLink,
  Camera,
  Heart,
  Accessibility,
  Settings,
  Image as ImageIcon,
  ArrowRight,
  Plane,
  Leaf
} from "lucide-react";
import { toast } from "sonner";

// Category configurations with demo paths
const CATEGORIES = {
  hair: {
    icon: Scissors,
    color: "from-blue-500 to-teal-500",
    lightColor: "from-blue-100 to-teal-100",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
    textColor: "text-blue-700",
    title: "Hair",
    demoPath: "/hair",
    providerLabel: "Hair Stylist"
  },
  nails: {
    icon: Sparkles,
    color: "from-pink-500 to-purple-500",
    lightColor: "from-pink-100 to-purple-100",
    bgColor: "bg-pink-50",
    borderColor: "border-pink-200",
    textColor: "text-pink-700",
    title: "Nails",
    demoPath: "/nail",
    providerLabel: "Nail Tech"
  },
  makeup: {
    icon: Palette,
    color: "from-purple-500 to-indigo-500",
    lightColor: "from-purple-100 to-indigo-100",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-200",
    textColor: "text-purple-700",
    title: "Makeup",
    demoPath: "/mua",
    providerLabel: "Makeup Artist"
  },
  lash: {
    icon: Eye,
    color: "from-amber-500 to-orange-500",
    lightColor: "from-amber-100 to-orange-100",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-200",
    textColor: "text-amber-700",
    title: "Lashes",
    demoPath: "/lash",
    providerLabel: "Lash Artist"
  },
  events: {
    icon: PartyPopper,
    color: "from-orange-500 to-red-500",
    lightColor: "from-orange-100 to-red-100",
    bgColor: "bg-orange-50",
    borderColor: "border-orange-200",
    textColor: "text-orange-700",
    title: "Events",
    demoPath: "/events",
    providerLabel: "Event Host"
  },
  wellness: {
    icon: Leaf,
    color: "from-teal-500 to-green-500",
    lightColor: "from-teal-100 to-green-100",
    bgColor: "bg-teal-50",
    borderColor: "border-teal-200",
    textColor: "text-teal-700",
    title: "Wellness",
    demoPath: "/wellness",
    providerLabel: "Wellness Provider"
  },
  travel: {
    icon: Plane,
    color: "from-sky-500 to-blue-500",
    lightColor: "from-sky-100 to-blue-100",
    bgColor: "bg-sky-50",
    borderColor: "border-sky-200",
    textColor: "text-sky-700",
    title: "Travel",
    demoPath: "/travel",
    providerLabel: "Travel Provider"
  }
};

const PublicProfilePage = () => {
  const { shareId } = useParams();
  const [searchParams] = useSearchParams();
  const providerType = searchParams.get('provider');
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const cardRef = useRef(null);
  
  // Get provider config from URL param
  const providerConfig = providerType ? CATEGORIES[providerType] : null;

  useEffect(() => {
    fetchProfile();
  }, [shareId]);

  // Update page title when profile loads
  useEffect(() => {
    if (profile?.name) {
      document.title = `phygital - ${profile.name}'s Preferences`;
    } else {
      document.title = "phygital - Shared Profile";
    }
  }, [profile]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/p/${shareId}`);
      setProfile(response.data);
    } catch (err) {
      console.error("Error fetching profile:", err);
      if (err.response?.status === 410) {
        setError("expired");
      } else if (err.response?.status === 404) {
        setError("not_found");
      } else {
        setError("unknown");
      }
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setLinkCopied(true);
    toast.success("Link copied!");
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const downloadCard = async () => {
    if (!cardRef.current) return;
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: '#f9fafb',
        scale: 2
      });
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `${profile?.name?.replace(/\s+/g, '-') || 'guest'}-preferences.png`;
      link.click();
      toast.success("Card downloaded!");
    } catch (err) {
      toast.error("Could not download card");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-gray-200 border-t-gray-900 rounded-full animate-spin" />
          <p className="text-gray-600 text-sm">Loading preferences...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            {error === "expired" ? "Link Expired" : error === "not_found" ? "Profile Not Found" : "Something Went Wrong"}
          </h1>
          <p className="text-gray-600 mb-6">
            {error === "expired" 
              ? "This preference profile link has expired. Ask them to share a new link."
              : error === "not_found"
              ? "This profile doesn't exist or has been removed."
              : "We couldn't load this profile. Please try again."}
          </p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-full hover:bg-gray-800 transition-colors">
            <PhygitalIcon className="w-5 h-5" variant="light" />
            Go to Phygital
          </Link>
        </div>
      </div>
    );
  }

  const category = profile?.category || "nails";
  const config = CATEGORIES[category] || CATEGORIES.nails;
  const CategoryIcon = config.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50" data-testid="public-profile-page">
      <PageMeta 
        title={profile?.business_name || profile?.name || "Provider Profile"}
        description={`${profile?.business_name || profile?.name || "Provider"} on Phygital - ${config?.label || category} professional`}
        image={profile?.profile_image || profile?.logo}
      />
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <PhygitalIcon className="w-6 h-6" />
            <span className="font-semibold text-gray-900">Phygital</span>
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={copyLink} className="gap-1.5" data-testid="copy-link-btn">
              {linkCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              <span className="hidden sm:inline">{linkCopied ? "Copied!" : "Copy Link"}</span>
            </Button>
            <Button variant="outline" size="sm" onClick={downloadCard} className="gap-1.5" data-testid="download-card-btn">
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Save</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Preference Card - Shareable/Downloadable */}
        <div 
          ref={cardRef}
          className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden mb-6"
          data-testid="preference-card"
        >
          {/* Card Header with Gradient */}
          <div className={`bg-gradient-to-r ${config.color} px-6 py-8 text-white relative overflow-hidden`}>
            {/* Shimmer effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 animate-pulse" />
            
            <div className="relative flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <CategoryIcon className="w-5 h-5" />
                  <span className="text-sm font-medium opacity-90">{config.title} Preferences</span>
                </div>
                <h1 className="text-2xl font-bold">{profile?.name || "Guest"}</h1>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <span className="text-2xl font-bold">{profile?.name?.charAt(0)?.toUpperCase() || "G"}</span>
              </div>
            </div>
          </div>

          {/* Card Body */}
          <div className="p-6">
            {/* Quick Preferences Grid */}
            {profile?.preferences && Object.keys(profile.preferences).length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Preferences
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  {Object.entries(profile.preferences).slice(0, 6).map(([key, value]) => {
                    if (!value) return null;
                    return (
                      <div key={key} className={`${config.bgColor} rounded-xl p-3 text-center`}>
                        <p className="text-xs text-gray-600 capitalize mb-0.5">{key.replace(/_/g, " ")}</p>
                        <p className={`font-semibold text-sm ${config.textColor}`}>
                          {Array.isArray(value) ? value.join(", ") : typeof value === "boolean" ? (value ? "Yes" : "No") : value}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Colors */}
            {profile?.colors?.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  Saved Colors
                </h3>
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {profile.colors.map((color, idx) => (
                    <div key={idx} className="flex-shrink-0 text-center">
                      <div 
                        className="w-14 h-14 rounded-xl shadow-md border-2 border-white mb-1"
                        style={{ backgroundColor: color.hex_code || "#888" }}
                      />
                      <p className="text-xs font-medium text-gray-900 truncate max-w-[60px]">{color.name}</p>
                      <p className="text-[10px] text-gray-600">{color.brand}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Designs/Inspiration */}
            {profile?.designs?.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <ImageIcon className="w-4 h-4" />
                  Inspiration
                </h3>
                <div className="grid grid-cols-3 gap-2">
                  {profile.designs.slice(0, 3).map((design, idx) => (
                    <div key={idx} className="aspect-square rounded-xl overflow-hidden bg-gray-100">
                      <img src={design.image_url || design} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Accessibility Note */}
            {profile?.accessibility && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
                <Accessibility className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900 text-sm">Note for Provider</p>
                  <p className="text-sm text-blue-700">{profile.accessibility}</p>
                </div>
              </div>
            )}

            {/* Phygital Branding */}
            <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-500">
                <PhygitalIcon className="w-5 h-5 opacity-50" />
                <span className="text-xs">Powered by Phygital</span>
              </div>
              <span className="text-[10px] text-gray-500 font-mono">{shareId}</span>
            </div>
          </div>
        </div>

        {/* Provider CTA - Dynamic based on provider type from URL */}
        <div className={`rounded-2xl p-6 text-white ${
          providerConfig 
            ? `bg-gradient-to-br ${providerConfig.color}` 
            : "bg-gradient-to-br from-gray-900 to-gray-800"
        }`}>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
              {providerConfig ? <providerConfig.icon className="w-6 h-6" /> : <Heart className="w-6 h-6" />}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg mb-1">
                {providerConfig 
                  ? `Are you a ${providerConfig.providerLabel}?`
                  : "Receive client preferences"
                }
              </h3>
              <p className="text-sm text-white/80 mb-4">
                {providerConfig 
                  ? `See how phygital helps ${providerConfig.providerLabel.toLowerCase()}s receive client preferences before appointments.`
                  : "Join phygital and let your clients share their preferences before appointments. No more guessing!"
                }
              </p>
              <Link
                to={providerConfig?.demoPath || "/login?type=stylist"}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-gray-900 rounded-full font-medium hover:bg-gray-100 transition-colors text-sm"
                data-testid="provider-cta-btn"
              >
                {providerConfig ? `See ${providerConfig.title} Demo` : "Get Started Free"}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 border-t border-gray-100 bg-white mt-8">
        <div className="max-w-2xl mx-auto px-4">
          <PoweredByPhygital />
        </div>
      </footer>
    </div>
  );
};

export default PublicProfilePage;
